import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Yellow ribbon at the top of every page when the visitor is signed in
 * to a demo sandbox. Shows a live countdown (re-renders every minute)
 * and a "Sign up to keep this" CTA. Closeable per-session via dismiss
 * state in sessionStorage — so it doesn't nag, but reappears on a
 * fresh tab so they don't forget they're in a demo.
 */
const DemoBanner = () => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [dismissed, setDismissed] = useState(
    sessionStorage.getItem('demo_banner_dismissed') === '1',
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    axios
      .get(`${BACKEND_URL}/api/demo/status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => !cancelled && r.data?.is_demo && setMeta(r.data))
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!meta) return;
    const t = setInterval(() => setNow(Date.now()), 60_000);  // tick every minute
    return () => clearInterval(t);
  }, [meta]);

  // Push the navbar (which is `fixed top-0`) below the banner via a CSS
  // variable read by Navbar's inline style. Restore on unmount/dismiss.
  useEffect(() => {
    if (!meta || dismissed) {
      document.documentElement.style.removeProperty('--demo-banner-h');
      return undefined;
    }
    document.documentElement.style.setProperty('--demo-banner-h', '36px');
    return () => document.documentElement.style.removeProperty('--demo-banner-h');
  }, [meta, dismissed]);

  if (!meta || !meta.is_demo || dismissed) return null;

  const expiresMs = meta.expires_at ? new Date(meta.expires_at).getTime() : null;
  let countdown = 'expires soon';
  if (expiresMs) {
    const diff = expiresMs - now;
    if (diff <= 0) countdown = 'expired';
    else {
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      countdown = h > 0 ? `${h}h ${m}m left` : `${m}m left`;
    }
  }

  return (
    <div
      className="bg-amber-400 text-stone-900 border-b border-amber-500 fixed top-0 left-0 right-0 z-[60] h-9"
      data-testid="demo-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 flex-shrink-0" />
          <span className="font-semibold">DEMO MODE</span>
          <span className="hidden sm:inline text-stone-800">·</span>
          <span className="hidden sm:inline truncate">
            Your sandbox <strong>{countdown}</strong>. Anything you change resets when it expires.
          </span>
          <span className="sm:hidden truncate text-xs">
            <strong>{countdown}</strong>
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => navigate('/demo/claim')}
            className="font-semibold underline hover:text-purple-800 transition-colors text-xs sm:text-sm"
            data-testid="demo-banner-signup-btn"
          >
            Keep my workspace →
          </button>
          <button
            onClick={() => {
              sessionStorage.setItem('demo_banner_dismissed', '1');
              setDismissed(true);
            }}
            aria-label="Dismiss"
            data-testid="demo-banner-dismiss-btn"
            className="p-1 hover:bg-amber-500/30 rounded transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DemoBanner;
